<script setup lang="ts">
import AvatarImage from '@/components/images/AvatarImage.vue';
import { routenames } from '@/router/routenames';
import { useAccountStore } from '@/stores/accountStore';
import { signOut, userPreferences } from '@/components/icons/icons';
import { mergeProps } from 'vue';
import { mdiInformation, mdiFileDocumentOutline } from '@mdi/js';
import { injectStrict } from '@/utils/injectStrict';
import { aboutDialogInjectionKey } from '@/components/AboutDialog/utils';
import HandbookDownload from '@/components/AboutDialog/HandbookDownload.vue';

const accountStore = useAccountStore();
const { isOpen } = injectStrict(aboutDialogInjectionKey);
</script>

<template>
  <v-menu transition="fade-transition">
    <template #activator="{ props: menuProps, isActive }">
      <span :class="['userAvatar', isActive ? 'userAvatar--active' : '']">
        <AvatarImage
          v-if="accountStore.currentUser"
          :hideTooltip="isActive"
          :tooltip="
            $t('PreferencesOfName', {
              name: accountStore.currentUser.fullName,
            })
          "
          v-bind="mergeProps(menuProps, $attrs)"
          :pictureId="accountStore.currentUser.profilePictureId"
        />
      </span>
    </template>
    <v-list class="list" bgColor="rgb(var(--v-theme-secondary-darken-4))">
      <v-list-item class="listItem text-body-2" :to="{ name: routenames.preferences }">
        <v-icon :icon="userPreferences" />
        {{ $t('Preferences') }}
      </v-list-item>

      <v-list-item class="listItem text-body-2">
        <HandbookDownload />
      </v-list-item>

      <v-list-item class="listItem text-body-2" :to="{ name: routenames.eula }">
        <v-icon :icon="mdiFileDocumentOutline" />
        {{ $t('EndUserLicenseAgreement') }}
      </v-list-item>

      <v-list-item class="listItem text-body-2" @click="isOpen = true">
        <v-icon :icon="mdiInformation" />
        {{ $t('AboutPtw100') }}
      </v-list-item>
      <v-divider class="divider" horizontal></v-divider>
      <v-list-item class="listItem text-body-2" :to="{ name: routenames.signOut }">
        <v-icon :icon="signOut" />
        {{ $t('SignOut') }}
      </v-list-item>
    </v-list>
  </v-menu>
</template>

<style lang="scss" scoped>
.list {
  padding: 4px 8px 4px 8px;
  border-radius: 2px;
}

.listItem {
  color: rgb(var(--v-theme-surface));
  border-radius: 2px;
  margin-top: 4px;
  margin-bottom: 4px;
  align-items: center;

  .v-icon {
    margin-right: 8px;
  }
}

.listItem:active,
.listItem:hover {
  background: rgb(var(--v-theme-secondary-darken-3));
}

.divider {
  height: 1px;
  opacity: 30%;
  color: rgb(var(--v-theme-background));
}
</style>

<style lang="scss" scoped>
.userAvatar {
  margin-left: 16px;
  cursor: pointer;
  position: relative;
  line-height: 0;
  transform: translateZ(0);

  &:after {
    content: '';
    position: absolute;
    // extend with 1px to all sides to prevent unclean edges
    bottom: -1px;
    left: -1px;
    width: calc(100% + 2px);
    height: calc(100% + 2px);
    border-radius: 50%;
    border: 2px solid rgb(var(--v-theme-secondary-lighten-1));
    opacity: 0;
    transition: all 0.3s;
    z-index: 1000;
    pointer-events: none;
  }

  &.userAvatar--active:after,
  &:hover:after {
    opacity: 1;
  }
}
</style>
